import React from "react";
import {
  FileText,
  FileX2,
  ClipboardCheck,
  Gift,
  Percent,
  CloudUpload,
  Sparkles,
  MousePointerClick,
  ArrowRight,
} from "lucide-react";
import { Reveal } from "./motion";

/**
 * Seção de destaque do módulo Contabilidade Expert.
 * A IA Talos faz o trabalho pesado nos bastidores; o usuário gera tudo em 1 clique.
 */
const EXPERT_ITEMS = [
  {
    icon: FileText,
    title: "Holerites",
    description: "Recibos de pagamento gerados, conferidos e prontos para envio a cada colaborador.",
  },
  {
    icon: FileX2,
    title: "Rescisões",
    description: "Verbas rescisórias, multas e aviso prévio calculados automaticamente, sem planilhas manuais.",
  },
  {
    icon: ClipboardCheck,
    title: "Fechamento de Folha",
    description: "Apuração, conferência e fechamento do mês com validação inteligente de cada rubrica.",
  },
  {
    icon: Gift,
    title: "Cálculo de 13º Salário",
    description: "Provisão mensal e cálculo oficial do 13º, com parcelas e reflexos de encargos inclusos.",
  },
  {
    icon: Percent,
    title: "Cálculo de Comissões",
    description: "Comissões apuradas por vendedor, produto ou unidade — já integradas à folha do mês.",
  },
  {
    icon: CloudUpload,
    title: "Transmissão para o eSocial",
    description: "Eventos enviados ao governo com validação prévia, protocolo e comprovante arquivados.",
  },
];

export const AccountingExpert = () => {
  return (
    <section
      id="contabilidade-expert"
      className="relative bg-ice py-24 transition-colors duration-500 dark:bg-navy-950 md:py-32"
      data-testid="accounting-expert-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Section Header */}
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-navy-800 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-md dark:border-white/10">
            <Sparkles className="h-3.5 w-3.5 text-lavender" /> Novo módulo · Contabilidade Expert
          </div>
          <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
            Sua contabilidade preditiva e em tempo real.{" "}
            <span className="text-navy-500 dark:text-lavender">O MUFT substitui o seu contador.</span>
          </h2>
          <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
            A <strong className="text-navy-800 dark:text-white font-bold">IA Talos</strong> faz todo o
            trabalho pesado nos bastidores: analisa cada lançamento, antecipa obrigações e mantém as
            contas da sua empresa sempre em dia. Você precisa de apenas{" "}
            <strong className="text-navy-800 dark:text-white font-bold">1 clique</strong> para gerar:
          </p>
        </Reveal>

        {/* 6 funcionalidades geradas em 1 clique pela IA Talos */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {EXPERT_ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <Reveal
                key={item.title}
                delay={i * 0.06}
                className="group relative rounded-3xl border border-navy-100 bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-navy-200 hover:shadow-xl dark:border-white/10 dark:bg-navy-900/70"
                data-testid={"accounting-expert-item-" + item.title.toLowerCase().replace(/\s+/g, "-")}
              >
                {/* Badge "1 clique" */}
                <span className="absolute right-5 top-5 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  <MousePointerClick className="h-3 w-3" /> 1 clique
                </span>

                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy-800 text-lavender shadow-lg shadow-navy-800/20 transition-transform duration-300 group-hover:scale-105 dark:bg-white dark:text-navy-900">
                  <Icon className="h-6 w-6" />
                </div>

                <h3 className="mt-5 text-lg font-black tracking-tight text-navy-800 dark:text-white">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm font-medium leading-relaxed text-navy-400 dark:text-navy-100/60">
                  {item.description}
                </p>
              </Reveal>
            );
          })}
        </div>

        {/* Footer da seção */}
        <Reveal delay={0.3} className="mt-16 text-center">
          <p className="text-sm font-bold uppercase tracking-wider text-navy-300 dark:text-navy-100/40">
            Sem planilhas. Sem idas ao contador. Sem surpresas no fim do mês.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default AccountingExpert;